import { useEffect } from 'react';
import { Option } from '@fluentui/react-components';
import { Control, FieldErrors, FieldPath, FieldValues, UseFormTrigger, UseFormWatch } from 'react-hook-form';

import { ControlledField } from '@shared/components';
import { DEFAULT_OPTION_LABEL } from '@shared/i18n';

import {
  DECISION_OPTIONS,
  EXCLUSION_REASON_ENABLED,
  EXCLUSION_REASON_OPTIONS,
  REVIEWING_STAGE_OPTIONS,
} from '../constants';
import { EAssessmentDecision, EExclusionReason } from '../types';
import { getDecisionLabel, getExclusionReasonLabel, getReviewingStageLabel } from '../helpers';

type TProps<T extends FieldValues> = {
  control: Control<T>;
  errors: FieldErrors<T>;
  watch: UseFormWatch<T>;
  trigger: UseFormTrigger<T>;
};

const AssessmentFields = <T extends FieldValues>({ control, errors, watch, trigger }: TProps<T>) => {
  // Cast field names once so rhf accepts them against the caller's generic form type.
  const decisionPath = 'decision' as FieldPath<T>;
  const reviewingStagePath = 'reviewingStage' as FieldPath<T>;
  const exclusionReasonPath = 'exclusionReason' as FieldPath<T>;
  const commentPath = 'comment' as FieldPath<T>;

  const decisionValue = watch(decisionPath) as unknown as string[] | undefined;
  const exclusionReasonValue = watch(exclusionReasonPath) as unknown as string[] | undefined;
  const showExclusionReason = EXCLUSION_REASON_ENABLED && decisionValue?.[0] === EAssessmentDecision.EXCLUDED;
  const commentRequired = showExclusionReason && exclusionReasonValue?.[0] === EExclusionReason.OTHER;

  // Re-validate comment when decision / exclusionReason change; onBlur mode doesn't re-run cross-field yup rules.
  useEffect(() => {
    const subscription = watch((_, { name, type }) => {
      if (type !== 'change') return;
      if (name === decisionPath || name === exclusionReasonPath) void trigger(commentPath);
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  return (
    <>
      <div className="flex items-start gap-3">
        <ControlledField
          label="Decision"
          name={decisionPath}
          control={control}
          errors={errors}
          required
          inputType="dropdown"
          getLabel={getDecisionLabel}
          fieldClassName="flex-1"
        >
          {DECISION_OPTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value} text={opt.label}>
              {opt.label}
            </Option>
          ))}
        </ControlledField>

        <ControlledField
          label="Reviewing stage"
          name={reviewingStagePath}
          control={control}
          errors={errors}
          inputType="dropdown"
          getLabel={getReviewingStageLabel}
          placeholder={DEFAULT_OPTION_LABEL}
          fieldClassName="flex-1"
        >
          <Option key="empty" value="" text={DEFAULT_OPTION_LABEL}>
            {DEFAULT_OPTION_LABEL}
          </Option>
          {REVIEWING_STAGE_OPTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value} text={opt.label}>
              {opt.label}
            </Option>
          ))}
        </ControlledField>
      </div>

      {showExclusionReason && (
        <ControlledField
          label="Exclusion reason"
          name={exclusionReasonPath}
          control={control}
          errors={errors}
          required
          inputType="dropdown"
          getLabel={getExclusionReasonLabel}
          placeholder="Select reason"
        >
          {EXCLUSION_REASON_OPTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value} text={opt.label}>
              {opt.label}
            </Option>
          ))}
        </ControlledField>
      )}

      <ControlledField
        label="Comment"
        name={commentPath}
        control={control}
        errors={errors}
        required={commentRequired}
        inputType="textarea"
        placeholder={commentRequired ? 'Describe the reason for exclusion' : 'Optional comment'}
      />
    </>
  );
};

export default AssessmentFields;
